/**
 * DebtKpiStrip — the four numbers that open the Liabilities tab.
 *
 * Total outstanding · Monthly committed · Interest still to come · Current
 * payoff date. Straight from the canonical plan, with the same honesty rules as
 * everywhere else: amounts in different currencies are listed, never added
 * (the one exception is the outstanding total, which the position resolver
 * already converts, and it says so), and no payoff date appears unless every
 * debt is in the projection.
 */
import React from 'react';
import { formatMoney } from '@/lib/format';
import { longMonth, horizon } from '@/lib/debtPlanFormat';
import { useDebtPlan } from './DebtPlanContext';
import { SectionError, StripSkeleton, useLoadingPhase } from '@/components/common/SectionLoading';

function Cell({ label, value, note, testid, muted }) {
  return (
    <div className="px-4 py-3.5 sm:px-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
        {label}
      </p>
      <p className={`font-display mt-1 leading-tight ${muted ? 'text-base text-muted-foreground' : 'text-xl text-charcoal'}`}>
        {value}
      </p>
      {note && <p className="text-xs text-muted-foreground mt-0.5">{note}</p>}
    </div>
  );
}

function MoneyLines({ byCurrency }) {
  const rows = Object.entries(byCurrency || {});
  if (rows.length === 0) return <>—</>;
  return (
    <>
      {rows.map(([c, v], i) => (
        <span key={c} className="block">
          {formatMoney(v, c, { compact: true })}
          {i < rows.length - 1 ? '' : ''}
        </span>
      ))}
    </>
  );
}

export function DebtKpiStrip() {
  const { plan, busy, error, refreshing, retry } = useDebtPlan();
  const phase = useLoadingPhase(busy && !plan);
  if (!plan) {
    if (error) {
      return (
        <SectionError testid="debt-kpi-error" onRetry={retry}
                      title="We couldn't work out the cost of your debt just now." />
      );
    }
    return (
      <StripSkeleton phase={phase} testid="debt-kpi-loading"
                     label="Loading your debt summary"
                     message="Adding up what you owe and what it costs&hellip;" />
    );
  }
  if (plan.state === 'no_debt') return null;

  const { scope, payoff_path: path } = plan;
  const interest = plan.interest_cost?.currencies || [];
  const remaining = {};
  for (const g of interest) {
    if (g.remaining?.available) remaining[g.currency] = g.remaining.amount;
  }
  const commitments = scope.monthly_commitment_by_currency || {};

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-border bg-card rounded-2xl border border-border shadow-sm mb-5"
         data-testid="debt-kpi-strip" aria-busy={refreshing}>
      <Cell
        testid="debt-kpi-outstanding"
        label={scope.group_label ? `${scope.group_label} outstanding` : 'Total outstanding'}
        value={scope.total_outstanding_display != null
          ? formatMoney(scope.total_outstanding_display, scope.display_currency, { compact: true })
          : <MoneyLines byCurrency={scope.outstanding_by_currency} />}
        note={scope.total_outstanding_display != null && scope.converted
          ? (Object.keys(scope.outstanding_by_currency || {}).length === 1
            ? `${formatMoney(Object.values(scope.outstanding_by_currency)[0],
                             Object.keys(scope.outstanding_by_currency)[0], { compact: true })}, converted to ${scope.display_currency}`
            : `Converted to ${scope.display_currency}`)
          : `Across ${scope.active_debts} debt${scope.active_debts === 1 ? '' : 's'}`}
      />
      <Cell
        testid="debt-kpi-committed"
        label="Monthly committed"
        value={<MoneyLines byCurrency={commitments} />}
        note={Object.keys(commitments).length > 1
          ? 'Listed per currency, not added'
          : 'Recorded payments each month'}
      />
      <Cell
        testid="debt-kpi-interest-remaining"
        label="Interest still to come"
        value={Object.keys(remaining).length
          ? <MoneyLines byCurrency={remaining} />
          : 'Not available'}
        note={Object.keys(remaining).length
          ? 'Estimated at today\u2019s rates'
          : 'Needs a rate and a payment on record'}
      />
      <Cell
        testid="debt-kpi-payoff-date"
        label={scope.group_label ? `${scope.group_label} payoff date` : 'Current payoff date'}
        muted={!path.headline_eligible}
        value={path.headline_eligible ? longMonth(path.payoff_date) : 'Not yet'}
        note={path.headline_eligible
          ? `${horizon(path.months)} at your current payments`
          : `Covers ${path.coverage_label}`}
      />
    </div>
  );
}

export default DebtKpiStrip;
